import { Box, Container, Grid2, CircularProgress } from '@mui/material';
import { HomeHeader } from '../../../components/new/headers/HomeHeader';
import { Link, useParams } from 'react-router-dom';
import { printEventDates } from '../../../components/commonFunc';
import { useEffect, useState, useMemo } from 'react';
import { Footer } from '../../../components/footers/Footer';
import HouseOutlinedIcon from '@mui/icons-material/HouseOutlined';
import RoomIcon from '@mui/icons-material/Room';
import PhoneSharpIcon from '@mui/icons-material/PhoneSharp';
import { CalendarDownMenu } from '../../../components/Contents/blogspage/CalendarDownMenu';
import { APILoadingStatus, Map, Marker, useApiLoadingStatus } from '@vis.gl/react-google-maps';
import { geocode, RequestType } from 'react-geocode';
import { getEventDetail } from '../api/api';
import { getEventGraphic2Url, getMediaDisplaySrc, resolveContentMedia } from '../helper/graphic2';
import { ListingCardImage } from '../layout/ListingCardImage';
import { PageMain } from '../../../components/seo/PageMain';
import { DetailPageHelmet } from '../helper/DetailPageHelmet';
import { getPublicSiteUrl } from '../siteUrl';
import { documentTitle, metaDescriptionFromRecord } from '../helper/detailSeo';

function getAddressText(addressData) {
  if (!addressData) return '';
  return [addressData.address, addressData.city, addressData.state, addressData.zip, addressData.country]
    .filter(Boolean)
    .join(', ');
}

function mapEventRow(eventRes) {
  return {
    postId: eventRes.id,
    title: eventRes.Title,
    seoTitle: eventRes.SeoTitle,
    metaDescription: eventRes.MetaDescription,
    postMedia: resolveContentMedia(eventRes, getEventGraphic2Url),
    postType: eventRes.ContentType,
    postDate: eventRes.LastUpdated,
    postName: eventRes.Relevance,
    eventStartDate: eventRes.StartDate,
    eventEndDate: eventRes.EndDate,
    eventUrl: eventRes.EventPath,
    description: eventRes.Description,
    addressData: {
      address: eventRes.Address,
      country: eventRes.Country,
      city: eventRes.City,
      state: eventRes.State,
      zip: eventRes.Zip,
      phone: eventRes.Phone,
    },
  };
}

export function EventDetail() {
  const { eventName } = useParams();
  const [eventData, setEventData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [markerLocation, setMarkerLocation] = useState({
    lat: 51.509865,
    lng: -0.118092,
  });

  const siteUrl = getPublicSiteUrl();
  const canonicalUrl = useMemo(() => {
    if (!siteUrl || !eventName) return '';
    return `${siteUrl}/articles/event/${encodeURIComponent(eventName)}`;
  }, [siteUrl, eventName]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setLoading(true);
    setEventData(null);
    setMapReady(false);
    const name = eventName;
    (async () => {
      if (!name) {
        if (!cancelled) {
          setLoadError('Missing event');
          setLoading(false);
        }
        return;
      }
      try {
        const eventRes = await getEventDetail(name);
        if (cancelled) return;
        const eventObj = mapEventRow(eventRes);
        if (eventObj.addressData) {
          const addressStr = `${eventObj.addressData.address || ''} ${eventObj.addressData.city || ''} ${eventObj.addressData.state || ''} ${eventObj.addressData.zip || ''} ${eventObj.addressData.country || ''}`;
          geocode(RequestType.ADDRESS, addressStr, {
            key: 'AIzaSyAOVYRIgupAurZup5y1PRh8Ismb1A3lLao',
            language: 'en',
            region: 'us',
          })
            .then((response) => {
              if (cancelled) return;
              const { lat, lng } = response.results[0].geometry.location;
              setMarkerLocation({ lat, lng });
            })
            .catch(() => {});
        }
        setEventData(eventObj);
      } catch (e) {
        if (!cancelled) {
          setLoadError('Event not found');
          setEventData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventName]);

  const metaDescription = useMemo(() => {
    if (!eventData) return 'Charlee.ai events';
    return metaDescriptionFromRecord({
      metaDescription: eventData.metaDescription,
      description: eventData.description,
      title: eventData.title,
      fallback: `${eventData.title} — ${printEventDates(eventData)}`,
    });
  }, [eventData]);

  const ogImage = useMemo(() => {
    return getMediaDisplaySrc(eventData?.postMedia);
  }, [eventData]);

  const pageTitle = eventData
    ? documentTitle(eventData.seoTitle, eventData.title, 'Event')
    : 'Event | Charlee.ai';
  const addressText = getAddressText(eventData?.addressData);
  const mapLoadingStatus = useApiLoadingStatus();
  const hasMapLoadError =
    mapLoadingStatus === APILoadingStatus.AUTH_FAILURE ||
    mapLoadingStatus === APILoadingStatus.FAILED;

  const breadcrumbItems = useMemo(() => {
    if (!eventData?.title) return undefined;
    return [
      { name: 'Home', path: '/' },
      { name: 'Events', path: '/articles/events' },
      { name: eventData.title, url: canonicalUrl },
    ];
  }, [eventData, canonicalUrl]);

  return (
    <div className="w-full overflow-hidden bg-[#F3F4F4]">
      {!loading && (
        <DetailPageHelmet
          pageTitle={pageTitle}
          metaDescription={metaDescription}
          canonicalUrl={canonicalUrl}
          ogImage={ogImage}
          ogType="article"
          noIndex={Boolean(loadError)}
          articlePublishedTime={eventData?.postDate || eventData?.eventStartDate}
          breadcrumbItems={breadcrumbItems}
          seoReady={!loading}
        />
      )}

      <HomeHeader headerColor="#F9F9F9" textColor="#021744" />
      <PageMain className="news-page bg-[#F9F9F9]">
        {loading && (
          <Box className="mt-16 flex min-h-[50vh] w-full items-center justify-center md:mt-24">
            <CircularProgress />
          </Box>
        )}
        {!loading && loadError && (
          <Box className="mt-20 w-full px-4 text-center md:mt-32">
            <p className="text-lg text-[#021744]">{loadError}</p>
            <Link to="/articles/events" className="text-[#22c0b1] underline mt-4 inline-block">
              Back to events
            </Link>
          </Box>
        )}
        {!loading && eventData && (
          <article className="mt-16 w-full md:mt-24">
            <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
              <Box className="w-full pt-6 md:pt-14">
                <Grid2 container spacing={{ xs: 3, sm: 4, md: 6, lg: 6, xl: 6 }}>
                  <Grid2 size={12}>
                    <div className="relative aspect-[300/200] w-full overflow-hidden rounded-2xl">
                      <ListingCardImage
                        mediaPath={eventData.postMedia}
                        title={eventData.title}
                        postType={eventData.postType || 'Events'}
                        alt={eventData.title ? `${eventData.title} — Charlee.ai event` : 'Charlee.ai event'}
                        containerClassName="w-full aspect-[300/200] rounded-2xl"
                        imageClassName="aspect-[300/200] w-full rounded-2xl object-cover"
                        iconClassName="h-12 w-12 text-[#0099B0]/70"
                        showFallbackLabel={false}
                        showFallbackTitle={false}
                        showFallbackMessage={false}
                      />
                      <Box
                        sx={{
                          background: 'linear-gradient(0deg, #22c0b1, #22c0b1, #ffffff00)',
                        }}
                        className="absolute bottom-0 w-full rounded-b-2xl px-4 py-4 md:py-9"
                      >
                        <h1 className="cursor-pointer text-2xl font-bold leading-tight tracking-normal text-white sm:text-3xl sm:leading-snug md:text-[42px] md:leading-[54.6px]">
                          {eventData?.title}
                        </h1>
                        <p className="cursor-pointer text-xl font-medium leading-tight text-white sm:text-2xl sm:leading-snug md:text-[42px] md:leading-[54.6px]">
                          {printEventDates(eventData)}
                        </p>
                      </Box>
                    </div>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <Box className="py-2 text-left md:py-3">
                      <div className="relative mb-5 h-[220px] w-full overflow-hidden rounded-[20px] bg-[#EAF6F4] shadow-sm sm:mb-7 sm:h-[260px] md:h-[300px]">
                        <Box
                          className="absolute inset-0 p-6"
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          textAlign="center"
                          sx={{
                            background:
                              'linear-gradient(135deg, #EAF9F7 0%, #F9F9F9 50%, #E9EEF8 100%)',
                          }}
                        >
                          <Box
                            className="mb-4 h-14 w-14 rounded-full bg-white text-[#22c0b1] shadow-sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <RoomIcon />
                          </Box>
                          <p className="text-base font-semibold leading-6 text-[#021744] md:text-[18px] md:leading-[24px]">
                            {hasMapLoadError ? 'Map Unavailable' : 'Event Location'}
                          </p>
                          {hasMapLoadError ? (
                            <p className="mt-3 text-[14px] leading-[22px] text-[#5F6B7A]">
                              We could not load the map right now. Please use the event address
                              below.
                              {addressText ? <span className="block mt-2">{addressText}</span> : null}
                            </p>
                          ) : addressText ? (
                            <p className="mt-3 text-[14px] leading-[22px] text-[#5F6B7A]">
                              {addressText}
                            </p>
                          ) : (
                            <p className="mt-3 text-[14px] leading-[22px] text-[#5F6B7A]">
                              Map details are loading.
                            </p>
                          )}
                        </Box>
                        {!hasMapLoadError && (
                          <Map
                            style={{
                              borderRadius: '20px',
                              height: '100%',
                              opacity: mapReady ? 1 : 0,
                              transition: 'opacity 200ms ease-in-out',
                            }}
                            defaultZoom={13}
                            defaultCenter={markerLocation}
                            gestureHandling={'greedy'}
                            disableDefaultUI
                            onIdle={() => setMapReady(true)}
                            onTilesLoaded={() => setMapReady(true)}
                          >
                            <Marker position={markerLocation} />
                          </Map>
                        )}
                      </div>
                      <CalendarDownMenu eventObj={eventData} />
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                    <Box className="py-2 text-left md:py-3">
                      <h3 className="py-2 text-lg font-semibold md:text-[22px]">
                        <HouseOutlinedIcon className="mx-2" />
                        WebSite
                      </h3>
                      <p className="pl-2">
                        <a
                          className="break-words underline text-[#22c0b1]"
                          href={eventData.eventUrl}
                          rel="noreferrer"
                        >
                          {eventData.eventUrl}
                        </a>
                      </p>

                      {eventData?.addressData && (
                        <>
                          <h3 className="py-2 text-lg font-semibold md:text-[22px]">
                            <RoomIcon className="mx-2" />
                            Address
                          </h3>
                          <h5 className="pl-2 font-semibold py-2">
                            {eventData.addressData.title}
                          </h5>
                          <p className="pl-2 underline">{eventData.addressData.address}</p>
                          <p className="pl-2 underline">
                            {eventData.addressData.city} {eventData.addressData.state}{' '}
                            {eventData.addressData.zip} {eventData.addressData.country}
                          </p>
                          <h3 className="py-2 text-lg font-semibold md:text-[22px]">
                            <PhoneSharpIcon className="mx-2" />
                            Phone
                          </h3>
                          <p className="pl-2 underline">{eventData.addressData.phone}</p>
                        </>
                      )}
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
            </Container>
          </article>
        )}
        {!loading && eventData && (
          <Box className="w-full bg-[#F3F4F4] py-12 md:py-20">
            <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
              <Box className="w-full">
                <Box
                  className="w-full"
                  display={'flex'}
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  <Link to="/articles/events" className="w-full sm:w-auto">
                    <button
                      type="button"
                      className="w-full rounded-full border border-[#021744] px-6 py-3 text-base font-medium leading-6 hover:bg-[#021744] hover:text-white sm:w-auto md:px-8 md:py-4 md:text-[18px]"
                    >
                      View all Events
                    </button>
                  </Link>
                </Box>
              </Box>
            </Container>
          </Box>
        )}
      </PageMain>
      <Footer />
    </div>
  );
}
